import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { BaseContainer } from '@base/base/BaseContainer'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { isPortrait } from '@/managers/ui/BackgroundManager'
import Facade from '@/Facade'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'

/** Keep in sync with ReelComponent "Reel Frame" position/scale settings. */
export const REEL_FRAME_BASE_LAYOUT = {
    landscape: { x: 434.5, y: -31, scaleX: 0.8, scaleY: 0.8 },
    portrait: { x: -39, y: 527, scaleX: 0.76, scaleY: 0.76 },
} as const

const MOBILE_LANDSCAPE_REEL_FRAME_X = 430.5
// After ModulesManager's settled resize (120ms) so we don't re-apply mid-orientation.
const REEL_FRAME_LAYOUT_SETTLE_S = 0.14

let reelFrameLayoutCall: gsap.core.Tween | null = null

export const applyMobileLandscapeReelFrameX = (container?: BaseContainer) => {
    if (!container) {
        return
    }

    if (!isPortrait() && DeviceUtils.isMobileDevice()) {
        container.x = MOBILE_LANDSCAPE_REEL_FRAME_X
    }
}

/**
 * Authoritative reel-frame placement from config coords. Avoids restoreBaseLayout snapshots
 * that can capture the previous orientation when gameResized fires before updateLayout.
 * Same layout in base and FS — the old FREE_SPINS_MACHINE_FRAME shrink desynced the frame
 * from the reels after Facade.container FS scaling was removed.
 */
export const applyReelFrameBaseLayout = (container?: BaseContainer) => {
    if (!container || (container as any).destroyed) {
        return
    }

    const layout = isPortrait() ? REEL_FRAME_BASE_LAYOUT.portrait : REEL_FRAME_BASE_LAYOUT.landscape
    container.position.set(layout.x, layout.y)
    container.scale.set(layout.scaleX, layout.scaleY)
    applyMobileLandscapeReelFrameX(container)
}

/** @deprecated Alias — frame uses base coords in every feature mode. */
export const applyReelFrameFeatureLayout = (container?: BaseContainer) => {
    applyReelFrameBaseLayout(container)
}

/**
 * ModulesManager emits gameResized before componentManager.updateLayout(), so any position
 * written in the signal handler is overwritten. Re-apply after layout settles.
 */
export const scheduleReelFrameLayoutSync = (container?: BaseContainer) => {
    if (!container || (container as any).destroyed) {
        return
    }

    reelFrameLayoutCall?.kill()
    reelFrameLayoutCall = gsap.delayedCall(REEL_FRAME_LAYOUT_SETTLE_S, () => {
        reelFrameLayoutCall = null
        if (!container || (container as any).destroyed) {
            return
        }

        applyReelFrameBaseLayout(container)
    })
}

export const applyReelFrameTexture = (element: any, textureName: string) => {
    const sprite = element as PIXI.Sprite
    if (!sprite || !(sprite instanceof PIXI.Sprite)) {
        return
    }

    sprite.texture = PIXI.Texture.from(textureName)
}

export const REEL_FRAME_ANIMATION_MAIN = 'main'
export const REEL_FRAME_ANIMATION_FREE = 'free'
const REEL_FRAME_ANIMATION_INTERVAL_MS = 10000

const reelFrameTimers = new WeakMap<SpineAnimation, ReturnType<typeof setInterval>>()

export const playReelFrameAnimation = (container: SpineAnimation): void => {
    if (!container.visible) {
        return
    }

    const isHoldAndWinActive = Boolean(Facade.model?.spinState?.isHoldAndWinActive) //|| Facade.model?.isBonusGameNow
    const animationName = isHoldAndWinActive ? REEL_FRAME_ANIMATION_FREE : REEL_FRAME_ANIMATION_MAIN

    container.alpha = 1
    container.start([animationName], [false], 0, [1])
}

/**
 * Stops the flourish, clears the repeat timer, and hides the spine so intro/popups
 * cannot be interrupted by a later interval tick forcing alpha back to 1.
 */
export const stopReelFrameAnimation = (container: SpineAnimation): void => {
    const timer = reelFrameTimers.get(container)
    if (timer) {
        clearInterval(timer)
        reelFrameTimers.delete(container)
    }

    try {
        container.stop()
    } catch {
        // Spine may already be idle.
    }

    container.visible = false
    container.alpha = 0
}

/**
 * Plays the reel-frame flourish immediately when base game is first shown,
 * then repeats every 10 seconds.
 */
export const startReelFrameAnimation = (container: SpineAnimation): void => {
    // Always attempt play — first splash call may no-op if the static frame is not
    // visible yet; the fade-in onStart call will succeed right after.
    playReelFrameAnimation(container)

    if (reelFrameTimers.has(container)) {
        return
    }

    reelFrameTimers.set(
        container,
        setInterval(() => {
            playReelFrameAnimation(container)
        }, REEL_FRAME_ANIMATION_INTERVAL_MS)
    )
}
